import Card from "@/UI/Card";
import Loader from "@/components/Loader/Loader";
import {
    deleteUniversity,
    loadSingleUniversity,
    updateUniversity,
    addMajorToUniversity,
    removeMajorFromUniversity,
    deleteUniversityAttachment,
} from "@/redux/rtk/features/CRM/university/universitySlice";
import {
    loadAllMajor,
    createMajor,
} from "@/redux/rtk/features/CRM/major/majorSlice";
import {
    updateMajorPricingInUniversity,
} from "@/redux/rtk/features/CRM/university/universitySlice";
import usePermissions from "@/utils/usePermissions";
import { Button, Form, Input, Select, Tag, Modal, Upload, InputNumber, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFillPencilFill } from "react-icons/bs";
import { PlusOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, FilePdfOutlined, YoutubeOutlined, EyeOutlined, EditOutlined, DollarOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { getAttachmentUrl } from "@/utils/functions";

export default function UniversityProfile({ data, loading }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [triggerSave, setTriggerSave] = useState(false);
    const [showMajorModal, setShowMajorModal] = useState(false);
    const [selectedMajors, setSelectedMajors] = useState([]);
    const [newMajorName, setNewMajorName] = useState("");
    const [isCreatingMajor, setIsCreatingMajor] = useState(false);
    const [fileList, setFileList] = useState([]);

    // Pricing modal state
    const [pricingModal, setPricingModal] = useState({ open: false, major: null });
    const [pricingForm, setPricingForm] = useState({ price: null, currency: 'USD', priceAfterScholarship: null, scholarships: [{ percentage: null, price: null }] });
    const [pricingLoading, setPricingLoading] = useState(false);

    const currencies = [
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' },
        { label: 'TRY', value: 'TRY' },
        { label: 'GBP', value: 'GBP' },
    ];

    const { list: majorList, loading: majorLoading } = useSelector(
        (state) => state.major
    );

    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const onDelete = async () => {
        Modal.confirm({
            title: t("university.deleteConfirm"),
            okText: t("common.yes"),
            okType: 'danger',
            cancelText: t("common.no"),
            onOk: async () => {
                const resp = await dispatch(deleteUniversity({ id }));
                if (resp.payload.message === "University deleted successfully") {
                    toast.success(t("university.universityDeletedSuccess"));
                    navigate("/admin/crm/university");
                }
            },
        });
    };

    const { permissions } = usePermissions();
    const canEdit = permissions?.includes("update-university");

    const onFinish = async (values) => {
        const formData = new FormData();
        formData.append("universityName", values.universityName || "");
        formData.append("email", values.email || "");
        formData.append("website", values.website || "");
        formData.append("youtubeLink", values.youtubeLink || "");
        formData.append("location", values.location || "");

        fileList.forEach((file) => {
            formData.append("file", file.originFileObj);
        });

        const resp = await dispatch(updateUniversity({ id: id, values: formData }));
        if (resp.payload.message === "success") {
            toast.success(t("university.universityUpdatedSuccess"));
            dispatch(loadSingleUniversity(id));
            setTriggerSave(false);
            setFileList([]);
        }
    };

    const onFinishFailed = (errorInfo) => {
        setTriggerSave(false);
        form.resetFields();
        console.log("Failed:", errorInfo);
    };

    const handleAddMajor = async () => {
        if (selectedMajors && selectedMajors.length > 0) {
            const resp = await dispatch(
                addMajorToUniversity({ id, majorIds: selectedMajors })
            );
            if (resp.payload.message === "success") {
                toast.success(t("university.majorsAddedSuccess"));
                // The slice now updates state.university directly, 
                // but we also call loadSingleUniversity to be 100% sure
                dispatch(loadSingleUniversity(id));
                setShowMajorModal(false);
                setSelectedMajors([]);
            }
        }
    };

    const handleCreateMajor = async () => {
        const trimmedName = newMajorName.trim();
        if (!trimmedName) return;

        // Check for duplicate name locally
        const existingMajor = majorList?.find(
            major => major.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (existingMajor) {
            toast.success(t("university.majorAlreadyExistsAndSelected") || "Major already exists and has been selected");
            if (!selectedMajors.includes(existingMajor.id)) {
                setSelectedMajors(prev => [...prev, existingMajor.id]);
            }
            setNewMajorName("");
            return;
        }

        setIsCreatingMajor(true);
        try {
            const resp = await dispatch(createMajor({ name: trimmedName }));
            if (resp.payload.message === "success") {
                toast.success(t("major.majorCreatedSuccess") || "Major Created Successfully");
                const newMajor = resp.payload.data;
                // Add the new major's ID to the selection list
                setSelectedMajors(prev => [...prev, newMajor.id]);
                setNewMajorName("");
                // Refresh the list from server to ensure Dropdown has it
                await dispatch(loadAllMajor({ query: "all" }));
            }
        } catch (error) {
            console.error("Failed to create major:", error);
        } finally {
            setIsCreatingMajor(false);
        }
    };

    const handleRemoveMajor = (majorId) => {
        Modal.confirm({
            title: t("university.removeMajorConfirm"),
            okText: t("common.yes"),
            okType: 'danger',
            cancelText: t("common.no"),
            onOk: async () => {
                const resp = await dispatch(
                    removeMajorFromUniversity({ id, majorId })
                );
                if (resp.payload.message === "success") {
                    toast.success(t("university.majorRemovedSuccess"));
                    dispatch(loadSingleUniversity(id));
                }
            },
        });
    };

    const openPricingModal = (major) => {
        let parsedScholarships = [{ percentage: null, price: null }];
        if (major.scholarships) {
            try {
                parsedScholarships = typeof major.scholarships === 'string' ? JSON.parse(major.scholarships) : major.scholarships;
                if (!Array.isArray(parsedScholarships) || parsedScholarships.length === 0) {
                    parsedScholarships = [{ percentage: null, price: null }];
                }
            } catch (e) { /* ignore */ }
        }
        setPricingForm({
            price: major.price || null,
            currency: major.currency || 'USD',
            priceAfterScholarship: major.priceAfterScholarship || major.price_after_scholarship || null,
            scholarships: parsedScholarships,
        });
        setPricingModal({ open: true, major });
    };

    const handlePricingScholarshipChange = (index, field, value) => {
        const updated = [...pricingForm.scholarships];
        updated[index] = { ...updated[index], [field]: value };
        setPricingForm(prev => ({ ...prev, scholarships: updated }));
    };

    const handleSavePricing = async () => {
        if (!pricingModal.major) return;
        setPricingLoading(true);
        const resp = await dispatch(updateMajorPricingInUniversity({
            universityId: id,
            majorId: pricingModal.major.id,
            values: {
                price: pricingForm.price,
                currency: pricingForm.currency,
                priceAfterScholarship: pricingForm.priceAfterScholarship,
                scholarships: pricingForm.scholarships.filter(s => s.percentage !== null && s.price !== null),
            }
        }));
        if (resp.payload.message === 'success') {
            toast.success(t("university.majorPricingUpdated") || 'Major pricing updated successfully');
            dispatch(loadSingleUniversity(id));
            setPricingModal({ open: false, major: null });
        }
        setPricingLoading(false);
    };

    const handleDeleteAttachment = (attachmentId) => {
        Modal.confirm({
            title: t("university.deleteAttachmentConfirm") || "Are you sure you want to delete this attachment?",
            okText: t("common.yes"),
            okType: 'danger',
            cancelText: t("common.no"),
            onOk: async () => {
                const resp = await dispatch(
                    deleteUniversityAttachment({ id, attachmentId })
                );
                if (resp.payload.message === "success") {
                    toast.success(t("university.attachmentDeletedSuccess") || "Attachment deleted successfully");
                    dispatch(loadSingleUniversity(id));
                }
            },
        });
    };

    useEffect(() => {
        dispatch(loadAllMajor({ query: "all" }));
    }, [dispatch]);

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                universityName: data.universityName,
                email: data.email,
                website: data.website,
                youtubeLink: data.youtubeLink || data.youtube_link,
                location: data.location,
            });
        }
    }, [data, form]);

    if (!data) return <Loader />;

    // Get available majors (not already added)
    const availableMajors = majorList?.filter(
        (major) => !data.majors?.some((m) => m.id === major.id)
    );

    return (
        <>
            <Card className="h-fit p-3 sm:p-4 md:p-6 border-none w-full">
                <Form
                    form={form}
                    colon={false}
                    disabled={!canEdit}
                    layout="vertical"
                    onFieldsChange={() => setTriggerSave(true)}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    initialValues={{
                        universityName: data?.universityName,
                        email: data?.email,
                        website: data?.website,
                        youtubeLink: data?.youtubeLink || data?.youtube_link,
                        location: data?.location,
                    }}
                >
                    {/* Profile Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start pb-4 sm:pb-6 border-b dark:border-gray-700 gap-4 sm:gap-0">
                        <div className="flex flex-col items-start w-full sm:w-auto">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                {data.universityName}
                            </h2>
                            {data.website && (
                                <a
                                    href={`https://${data.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mt-1"
                                >
                                    {data.website}
                                </a>
                            )}
                            {(data.youtubeLink || data.youtube_link) && (
                                <a
                                    href={data.youtubeLink || data.youtube_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs sm:text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 mt-1 flex items-center gap-1"
                                >
                                    <YoutubeOutlined /> {t("university.youtube_link")}
                                </a>
                            )}
                            {data.location && (
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    📍 {data.location}
                                </p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto sm:min-w-[120px]">
                            {triggerSave ? (
                                <>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="small"
                                        className="flex-1 sm:flex-none"
                                    >
                                        {t("university.saveChanges")}
                                    </Button>
                                    <Button
                                        loading={loading}
                                        onClick={onFinishFailed}
                                        size="small"
                                        className="flex-1 sm:flex-none"
                                    >
                                        {t("common.cancel")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    danger
                                    onClick={onDelete}
                                    size="small"
                                    className="w-full sm:w-auto"
                                >
                                    {t("university.deleteUniversity")}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="pt-4 sm:pt-6 space-y-3 sm:space-y-4">
                        <Form.Item
                            label={t("university.universityName")}
                            name="universityName"
                            className="mb-3 sm:mb-4"
                        >
                            <Input
                                bordered={false}
                                size="large"
                                suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
                                className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("common.email")}
                            name="email"
                            className="mb-3 sm:mb-4"
                        >
                            <Input
                                bordered={false}
                                size="large"
                                suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
                                className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("university.website")}
                            name="website"
                            className="mb-3 sm:mb-4"
                        >
                            <Input
                                bordered={false}
                                size="large"
                                suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
                                className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("university.youtube_link")}
                            name="youtubeLink"
                            className="mb-3 sm:mb-4"
                        >
                            <Input
                                bordered={false}
                                size="large"
                                suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
                                className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("university.location")}
                            name="location"
                            className="mb-3 sm:mb-4"
                        >
                            <Input
                                bordered={false}
                                size="large"
                                suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
                                className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
                            />
                        </Form.Item>

                        {/* PDF File Section */}
                        <div className="py-4 border-t dark:border-gray-700">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                                {t("university.universityPdf")}
                            </h3>

                            <div className="space-y-3 mb-4">
                                {/* Main file if exists and not in attachments list (backwards compatibility) */}
                                {data.file && !data.attachments?.some(a => a.filePath === data.file) && (
                                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                                        <div className="flex items-center gap-3">
                                            <FilePdfOutlined className="text-xl text-red-500" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {t("university.universityDocument")}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => window.open(getAttachmentUrl(data.file), '_blank')}
                                            />
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                href={getAttachmentUrl(data.file)}
                                                target="_blank"
                                                download
                                                className="!text-white"
                                            >
                                                {t("common.download")}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Multiple attachments */}
                                {data.attachments && data.attachments.length > 0 ? (
                                    data.attachments.map((att) => (
                                        <div key={att.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                                            <div className="flex items-center gap-3">
                                                <FilePdfOutlined className="text-xl text-red-500" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white max-w-[150px] truncate">
                                                        {att.fileName || t("university.universityDocument")}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => window.open(getAttachmentUrl(att.filePath), '_blank')}
                                                />
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    href={getAttachmentUrl(att.filePath)}
                                                    target="_blank"
                                                    download
                                                    className="!text-white"
                                                >
                                                    {t("common.download")}
                                                </Button>
                                                {canEdit && (
                                                    <Button
                                                        danger
                                                        type="text"
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => handleDeleteAttachment(att.id)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (!data.file && (
                                    <p className="text-sm text-gray-400 italic">
                                        {t("university.noPdfUploaded")}
                                    </p>
                                ))}
                            </div>

                            {canEdit && (
                                <Form.Item
                                    label={t("university.uploadPdf")}
                                    className="mb-0"
                                >
                                    <Upload
                                        beforeUpload={() => false}
                                        fileList={fileList}
                                        onChange={({ fileList }) => {
                                            setFileList(fileList);
                                            if (fileList.length > 0) setTriggerSave(true);
                                        }}
                                        accept=".pdf"
                                        multiple
                                    >
                                        <Button icon={<UploadOutlined />} size="middle">
                                            {t("university.selectPdf")}
                                        </Button>
                                    </Upload>
                                </Form.Item>
                            )}
                        </div>

                        {/* Majors Section */}
                        <div className="pt-4 border-t dark:border-gray-700">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    {t("university.majors")}
                                </h3>
                                {canEdit && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => setShowMajorModal(true)}
                                    >
                                        {t("university.addMajor")}
                                    </Button>
                                )}
                            </div>

                            <div className="space-y-2">
                                {data.majors && data.majors.length > 0 ? (
                                    data.majors.map((major) => {
                                        const hasPrice = major.price !== null && major.price !== undefined;
                                        return (
                                            <div
                                                key={major.id}
                                                className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 group hover:border-blue-200 dark:hover:border-blue-700 transition-colors"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">{major.name}</span>
                                                    {hasPrice ? (
                                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold whitespace-nowrap">
                                                            {Number(major.price).toLocaleString()} {major.currency || 'USD'}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 italic">{t("university.noPrice") || 'No price'}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {canEdit && (
                                                        <>
                                                            <Tooltip title={t("university.editPricing") || 'Edit Pricing'}>
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<DollarOutlined />}
                                                                    onClick={() => openPricingModal(major)}
                                                                    className="text-blue-500 hover:text-blue-700"
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title={t("common.remove")}>
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() => handleRemoveMajor(major.id)}
                                                                />
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-gray-400 text-sm">
                                        {t("university.noMajors")}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </Form>

                {/* Add Major Modal */}
                <Modal
                    title={t("university.addMajor")}
                    open={showMajorModal}
                    onOk={handleAddMajor}
                    onCancel={() => {
                        setShowMajorModal(false);
                        setSelectedMajors([]);
                        setNewMajorName("");
                    }}
                    width={600}
                    okText={t("common.add")}
                    cancelText={t("common.cancel")}
                >
                    <div className="space-y-6 px-1">
                        {/* Current Majors Display */}
                        <div>
                            <p className="text-sm font-medium text-gray-500 mb-2">
                                {t("university.currentlyLinkedMajors")}:
                            </p>
                            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 min-h-[50px]">
                                {data.majors && data.majors.length > 0 ? (
                                    data.majors.map((major) => (
                                        <Tag key={major.id} color="blue" className="px-3 py-1 text-sm border-blue-200">
                                            {major.name}
                                        </Tag>
                                    ))
                                ) : (
                                    <span className="text-gray-400 text-xs italic">
                                        {t("university.noMajorsLinked")}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Selection and New Major Section */}
                        <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-sm font-medium text-gray-500">
                                {t("university.selectMoreMajors")}:
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Select
                                    mode="multiple"
                                    size="large"
                                    loading={majorLoading}
                                    placeholder={t("university.selectMajor")}
                                    value={selectedMajors}
                                    onChange={setSelectedMajors}
                                    className="flex-1"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                >
                                    {availableMajors?.map((major) => (
                                        <Select.Option key={major.id} value={major.id}>
                                            {major.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                    </div>
                </Modal>

            </Card>

            {/* Pricing Modal */}
            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <DollarOutlined className="text-blue-500" />
                        <span>{t("university.editPricing") || 'Edit Major Pricing'}: {pricingModal.major?.name}</span>
                    </div>
                }
                open={pricingModal.open}
                onOk={handleSavePricing}
                onCancel={() => setPricingModal({ open: false, major: null })}
                confirmLoading={pricingLoading}
                okText={t("common.save") || 'Save'}
                cancelText={t("common.cancel")}
                width={520}
            >
                <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="mb-1 text-sm font-medium">{t("common.price") || 'Price'}:</p>
                            <InputNumber
                                size="large"
                                className="w-full"
                                min={0}
                                value={pricingForm.price}
                                onChange={(val) => setPricingForm(prev => ({ ...prev, price: val }))}
                                placeholder="e.g. 5000"
                            />
                        </div>
                        <div>
                            <p className="mb-1 text-sm font-medium">{t("common.currency") || 'Currency'}:</p>
                            <Select
                                size="large"
                                className="w-full"
                                value={pricingForm.currency}
                                onChange={(val) => setPricingForm(prev => ({ ...prev, currency: val }))}
                                options={currencies}
                            />
                        </div>
                    </div>



                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-medium">{t("university.scholarships") || 'Scholarships'}:</p>
                            <Button
                                type="dashed"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => setPricingForm(prev => ({ ...prev, scholarships: [...prev.scholarships, { percentage: null, price: null }] }))}
                            >
                                {t("university.addScholarship") || 'Add'}
                            </Button>
                        </div>
                        {pricingForm.scholarships.map((s, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                                <div className="col-span-5">
                                    <p className="mb-1 text-xs text-gray-500">{t("university.scholarshipPercentage") || 'Scholarship %'}:</p>
                                    <InputNumber
                                        className="w-full"
                                        min={0}
                                        max={100}
                                        formatter={v => `${v}%`}
                                        parser={v => v.replace('%', '')}
                                        value={s.percentage}
                                        onChange={(val) => handlePricingScholarshipChange(idx, 'percentage', val)}
                                        placeholder="e.g. 50"
                                    />
                                </div>
                                <div className="col-span-5">
                                    <p className="mb-1 text-xs text-gray-500">{t("university.priceAfterScholarship") || 'Price After'}:</p>
                                    <InputNumber
                                        className="w-full"
                                        min={0}
                                        value={s.price}
                                        onChange={(val) => handlePricingScholarshipChange(idx, 'price', val)}
                                        placeholder="Price"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Button
                                        danger
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        onClick={() => setPricingForm(prev => ({ ...prev, scholarships: prev.scholarships.filter((_, i) => i !== idx) }))}
                                        disabled={pricingForm.scholarships.length === 1}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    );
}
